import { Controller } from "@hotwired/stimulus"

const SAVE_DELAY = 300
const FONT_SIZE_LABELS = { xs: "XS", sm: "Small", md: "Medium", lg: "Large", xl: "XL" }

// Reader Settings (TMS-791). Owns the Course Tools panel, writes each setting
// onto the reader root as a data-reader-* attribute for the display rules to key
// off, saves the choice for the student's next visit, and drives the two reading
// aids that need to follow the pointer: focus mode and the reading ruler.
export default class extends Controller {
  static targets = ["toolsTrigger", "toolsPanel", "fontSizeSlider", "ruler"]
  static values = { saveUrl: String }

  connect() {
    this.checkSystemTheme()
    this.describeFontSize()
    this.refreshReadingAids()
  }

  disconnect() {
    this.stopTrackingPointer()
    if (this.saveTimeout) clearTimeout(this.saveTimeout)
  }

  // --- Course Tools panel ---------------------------------------------------

  toggleTools() {
    this.setToolsOpen(this.toolsPanelTarget.hidden)
  }

  closeTools() {
    if (this.toolsPanelTarget.hidden) return

    this.setToolsOpen(false)
  }

  closeToolsOnOutsideClick(event) {
    if (this.toolsPanelTarget.hidden) return
    if (event.target.closest?.(".academy-course-tools")) return

    this.setToolsOpen(false)
  }

  setToolsOpen(open) {
    this.toolsPanelTarget.hidden = !open
    this.toolsTriggerTarget.setAttribute("aria-expanded", String(open))
    this.save("tools_open", open)
  }

  // --- Controls -------------------------------------------------------------

  change(event) {
    const input = event.currentTarget
    this.apply(input.dataset.readerSetting, input.value)
  }

  changeFontSize() {
    this.apply("font_size", this.fontSize)
    this.describeFontSize()
  }

  changeFlag(event) {
    const input = event.currentTarget
    this.apply(input.dataset.readerSetting, input.checked)
    this.refreshReadingAids()
  }

  // Guarded because the segmented controls also apply on click, so the `change`
  // that follows the same click must not spend a second save on a value that is
  // already in place.
  apply(key, value) {
    const attribute = this.attributeFor(key)
    if (this.element.dataset[attribute] === String(value)) return

    this.element.dataset[attribute] = String(value)
    this.save(key, value)
  }

  // data-reader-font-size, data-reader-reading-ruler and friends, as the dataset
  // camelCase keys the display rules are written against.
  attributeFor(key) {
    const name = key === "reading_ruler" ? "ruler" : key
    return `reader${name.replace(/(^|_)([a-z])/g, (_match, _separator, letter) => letter.toUpperCase())}`
  }

  get fontSize() {
    return this.fontSizeSteps[Number(this.fontSizeSliderTarget.value)]
  }

  get fontSizeSteps() {
    return this.fontSizeSliderTarget.dataset.readerFontSizes.split(" ")
  }

  describeFontSize() {
    if (!this.hasFontSizeSliderTarget) return

    const size = this.fontSize
    this.fontSizeSliderTarget.setAttribute("aria-valuetext", FONT_SIZE_LABELS[size] || size)
  }

  // With no explicit choice the theme follows the browser, which the display
  // rules already do on their own. The control still has to show which side that
  // landed on, so the matching radio is checked once we can ask.
  checkSystemTheme() {
    if (this.element.dataset.readerTheme !== "system") return

    const preferred = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
    const radio = this.element.querySelector(`input[name="reader-theme"][value="${preferred}"]`)
    if (radio) radio.checked = true
  }

  // --- Focus mode and reading ruler -----------------------------------------

  refreshReadingAids() {
    this.markFocusUnits()

    if (this.focusModeOn || this.readingRulerOn) {
      this.startTrackingPointer()
      this.updateReadingAids(this.lastPointerY ?? window.innerHeight / 2)
    } else {
      this.stopTrackingPointer()
    }
  }

  get focusModeOn() {
    return this.element.dataset.readerFocusMode === "true"
  }

  get readingRulerOn() {
    return this.element.dataset.readerRuler === "true"
  }

  get contentElement() {
    return this.element.querySelector(".academy-reader-panel")
  }

  startTrackingPointer() {
    if (this.trackingPointer) return

    this.trackingPointer = true
    this.onPointerMove = (event) => this.updateReadingAids(event.clientY)
    // Nothing moves the pointer while the student scrolls, so the aids settle on
    // the middle of the viewport — where they are reading — rather than freezing
    // wherever the mouse happens to rest.
    this.onScroll = () => this.updateReadingAids(this.lastPointerY ?? window.innerHeight / 2)

    window.addEventListener("pointermove", this.onPointerMove, { passive: true })
    window.addEventListener("scroll", this.onScroll, { passive: true })
    window.addEventListener("resize", this.onScroll, { passive: true })
  }

  stopTrackingPointer() {
    if (!this.trackingPointer) return

    this.trackingPointer = false
    window.removeEventListener("pointermove", this.onPointerMove)
    window.removeEventListener("scroll", this.onScroll)
    window.removeEventListener("resize", this.onScroll)
  }

  updateReadingAids(pointerY) {
    this.lastPointerY = pointerY

    if (this.focusModeOn) this.focusNearestUnit(pointerY)
    if (this.readingRulerOn) this.positionRuler(pointerY)
  }

  // The units that fade: each paragraph of prose on its own, and any other block
  // as a whole, since a flip card or a quiz has no paragraphs to single out.
  markFocusUnits() {
    const content = this.contentElement
    if (!content) return

    this.focusUnits = Array.from(content.querySelectorAll(".academy-block")).flatMap((block) => {
      const paragraphs = Array.from(block.querySelectorAll(".academy-rich-text > p, .academy-rich-text > li"))
      return paragraphs.length > 0 ? paragraphs : [block]
    })

    this.focusUnits.forEach((unit) => unit.classList.add("is-reader-focus-unit"))
  }

  focusNearestUnit(pointerY) {
    const nearest = this.nearestUnit(pointerY)

    this.focusUnits.forEach((unit) => unit.classList.toggle("is-reader-focused", unit === nearest))
  }

  nearestUnit(pointerY) {
    let nearest = null
    let shortestDistance = Infinity

    this.focusUnits.forEach((unit) => {
      const box = unit.getBoundingClientRect()
      const distance = Math.max(box.top - pointerY, pointerY - box.bottom, 0)
      if (distance < shortestDistance) {
        shortestDistance = distance
        nearest = unit
      }
    })

    return nearest
  }

  positionRuler(pointerY) {
    if (!this.hasRulerTarget) return

    const content = this.contentElement
    if (!content) return

    // Clamped to the lesson: the ruler is a reading aid for the content, and
    // left to follow the pointer it would draw a line across the topbar.
    const box = content.getBoundingClientRect()
    const top = Math.min(Math.max(pointerY, box.top), box.bottom)

    this.rulerTarget.style.setProperty("--reader-ruler-top", `${Math.round(top)}px`)
    this.rulerTarget.style.setProperty("--reader-ruler-left", `${Math.round(box.left)}px`)
    this.rulerTarget.style.setProperty("--reader-ruler-width", `${Math.round(box.width)}px`)
  }

  // --- Persistence ----------------------------------------------------------

  // Debounced because the font size slider fires on every step it passes
  // through, and only the value the student lands on is worth a request.
  save(key, value) {
    this.pendingPreferences = { ...this.pendingPreferences, [key]: value }

    if (this.saveTimeout) clearTimeout(this.saveTimeout)
    this.saveTimeout = setTimeout(() => this.flushPreferences(), SAVE_DELAY)
  }

  // Fire and forget: the reader has already changed on screen, and a failed save
  // only costs the student the preference on their next visit.
  flushPreferences() {
    const preferences = this.pendingPreferences
    this.pendingPreferences = {}

    fetch(this.saveUrlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
      },
      credentials: "same-origin",
      body: JSON.stringify({ preferences })
    }).catch(() => {})
  }
}
